import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import ApprovalItemsStatus, {
  readItemLabels,
} from '@/components/Approval/@components/ApprovalItemsStatus';
import {
  countSalesContractCards,
  SalesContractSnapshot,
  type SalesContractCardRef,
} from '@/components/Approval/@components/SalesContract';
import usePartialReject, {
  getPartialRejectErrorMessage,
} from '@/hooks/approval/usePartialReject';
import useApprovalView from '@/hooks/approval/useApprovalView';
import { useSnackbar } from '@/hooks/global-tools';
import { MyInfoState } from '@/recoil/atom';
import { parseSalesContractContent } from '@/utils/approval/salesContract';
import { isCurrentApproverMeOf } from '../../actionVisibility';
import ReasonInputModal from '../../ReasonInputModal';
import type { ContentExtraProps } from '../../types';
import { ContentSlotRoot } from '../styles';

const REASON_MAX_LENGTH = 300;

/**
 * 136 GERP Sales Contract — 저장 카드 HTML 그대로(레거시 문서 포함), GERP 재조회 안 함.
 * 부분반려 버튼: 진행중 + 지금 내 결재 차례 + 카드 2장 이상(레거시 조건). 「추가 확인」은 결재 건별 현재 상태
 */
const SalesContractSlot = ({ data }: ContentExtraProps) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useSnackbar();
  const myInfo = useAtomValue(MyInfoState);
  const { data: view } = useApprovalView(data.documentId);
  const { partialReject, submitting } = usePartialReject(data.documentId);
  // 닫히는 동안 제목이 비지 않게 대상은 남겨 둠
  const [target, setTarget] = useState<SalesContractCardRef | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const content = parseSalesContractContent(data.attributes);
  const labels = useMemo(
    () =>
      readItemLabels(content, [
        ['data-item-id', 'data-label'],
        ['data-aprv_id', 'data-sales_contr_no'],
      ]),
    [content],
  );

  const canPartialReject =
    !!view &&
    view.status === 'P' &&
    isCurrentApproverMeOf(
      view.approvers,
      view.currentApproverOrder,
      myInfo?.passportId ?? '',
      view.status,
    ) &&
    countSalesContractCards(content) >= 2;

  const renderCardAction = useCallback(
    (card: SalesContractCardRef) =>
      canPartialReject ? (
        <BoxButton
          key="reject"
          type="button"
          variant="secondary"
          size="small"
          onClick={() => {
            setTarget(card);
            setIsModalOpen(true);
          }}
        >
          {t('부분반려')}
        </BoxButton>
      ) : null,
    [canPartialReject, t],
  );

  const handleSubmit = async (reason: string) => {
    if (!target) return;
    try {
      await partialReject(target.itemId, reason);
      setIsModalOpen(false);
      showSuccess({ message: t('부분 반려가 완료 되었습니다.') });
    } catch (error) {
      showError({ message: t(getPartialRejectErrorMessage(error)) });
    }
  };

  if (!content) return null;
  return (
    <ContentSlotRoot>
      <ApprovalItemsStatus
        documentId={data.documentId}
        buttonLabel="추가 확인"
        title="품번확인"
        labelHeader="No"
        portalRecordedStatus
        labels={labels}
      />
      <SalesContractSnapshot
        content={content}
        renderCardAction={renderCardAction}
      />
      <ReasonInputModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
        submitting={submitting}
        title={t('{{partcode}} {{aprvRev}}차 반려', {
          partcode: target?.label ?? '',
          aprvRev: target?.aprvRev ?? '',
        })}
        submitLabel={t('반려처리')}
        reasonLabel={t('사유입력')}
        reasonPlaceholder=""
        reasonMaxLength={REASON_MAX_LENGTH}
      />
    </ContentSlotRoot>
  );
};

export default SalesContractSlot;
